import { Suspense, useEffect, useMemo, useState } from 'react';
import { Box, Paper } from '@mui/material';
import CustomAccordion from '../../../../components/ui/materialui/customAccordion';
import Tabla from '../../../../components/ui/materialui/tabla';
import ConfirmationModal from '../../../../components/ui/materialui/confirmationModal';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../../hooks/useToast';
import { RowRecaudacionEndosoMasivo } from '../models/rowRecaudacionEndosoMasivo';
import FormularioArchivoEndoso from '../components/formularioArchivo';
import { InputArchivoEndosoMasivo } from '../models/inputArchivoEndosoMasivo';
import { MutuosARecaudar } from '../models/mutuosARecaudar';
import { UseColumns } from '../utils/columns';
import { FormularioCalculoRecaudacionEndoso } from '../components/formularioCalculoRecaudacion';
import { CalculateRecaudacionEM } from '../models/calculateRecaudacionEM';
import UseRecaudacionEndosoMasivo from '../hooks/useRecMaviaEndoso';
import { ModalErrorExcelMasivo } from '../components/modalErrorExcel';
import { UseColumnsResultados } from '../utils/columnsResultados';
import { ResultadoRecaudacionEndoso } from '../models/resultadoRecaudacionEndoso';
import { ModalResultados } from '../../../../components/ui/materialui/modalResultados';
import Breadcrumb from '../../../../components/ui/materialui/breadcrumb';
import { AxiosResponse } from 'axios';
import { ListResponseDto } from '../../../../dtos/ListResponseDto';
import { PermissionGuard } from '../../../../components/auth/ProtectedRoute';
import { TipoFuncionalidad } from '../../../../utils/enums';

export default function EndososPSM(){

    const { addToast } = useToast();
    const navigate = useNavigate();

    const { cargarArchivo, recaudarEndoso } = UseRecaudacionEndosoMasivo();

    const breadcrumbItems = [
        { label: 'Inicio', url: '/' },
        { label: 'Endosos', url: `/endosos` },
        { label: 'Recaudación Endoso Masivo', url: `/endosos/endosos-psm` }
    ]

    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    });

    const [data, setData] = useState<RowRecaudacionEndosoMasivo[]>([])
    const [mutuosRecaudar, setMutuosRecaudar] = useState<MutuosARecaudar[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [resultRecaudacion, setResultRecaudacion] = useState<ResultadoRecaudacionEndoso[]>([])
    const [showResult, setShowResult] = useState<boolean>(false);
    const [showError, setShowError] = useState<boolean>(false);
    const [errorProcesamiento, setErrorProcesamiento] = useState<string>("")

    const { COLUMNS } = UseColumns({ mutuosRecaudar, data, setMutuosRecaudar });
    const columns = useMemo(() => COLUMNS, [mutuosRecaudar, data, setMutuosRecaudar]);

    const { COLUMNS_RESULTADOS } = UseColumnsResultados();
    const columnsResultados = useMemo(() => COLUMNS_RESULTADOS, []);

    const onSubmit = async (e: InputArchivoEndosoMasivo) => {
        setData([])
        setMutuosRecaudar([])
        setIsLoading(true)
        try{
            const response = await cargarArchivo(e);
            const isResponseOk = response.status === 200 || response.status === 201;

            const errorArchivo = !isResponseOk && response.response && response.response.data.message.toString().includes("ERROR ARCHIVO")

            if (isResponseOk){
                setData(response.data.filas)
                if (response.data.erroresValidaciones && response.data.erroresValidaciones != ""){
                    setErrorProcesamiento(response.data.erroresValidaciones)
                    setShowError(true)
                }
            }
            else if (errorArchivo){
                setErrorProcesamiento(response.response.data.message)
                setShowError(true)
            }
            else if (response.response && response.response.data){
                addToast({
                    mostrar: true,
                    titulo: 'Error',
                    tipo: 'Danger',
                    mensaje: response.response.data.message,
                });
            }
            else{
                addToast({
                    mostrar: true,
                    titulo: 'Error',
                    tipo: 'Danger',
                    mensaje: 'Ha ocurrido un error'
                });
            }
        }
        catch(e){
            addToast({
                mostrar: true,
                titulo: 'Error',
                tipo: 'Danger',
                mensaje: 'Ha ocurrido un error'
            });
        }
        
        setIsLoading(false)
    }

    const onRecaudar = () => {
        setModalConfirmacion({
            show: true,
            title: 'Recaudar endosos masivos',
            message: "¿Está seguro que quiere recaudar los endosos seleccionados?",
            metodo: () => {onRecaudarAccept()}
        });
    }

    const onRecaudarAccept = async () => {
        const idsAEnviar = mutuosRecaudar.map((x) => x.mutuoId);
        const objetosAEnviar = data.filter((x) => idsAEnviar.includes(x.mutuoId));
        const response: AxiosResponse<ListResponseDto<ResultadoRecaudacionEndoso>> = await recaudarEndoso(objetosAEnviar);
        if (response.status === 200 || response.status === 201){
            const result = response.data.items;
            const messageOk = "Se registró la recaudación correctamente para este mutuo";
            const mutuosOK = result.filter(x => x.observacion.includes(messageOk)).map(x => x.numeroOperacion);
            const mutuosAMantener = data.filter(x => !mutuosOK.includes(x.numeroOperacion));
            const mutuosIDMantener = mutuosAMantener.map(x => x.mutuoId);
            const mutuosSeleccionados = mutuosRecaudar.filter(x => mutuosIDMantener.includes(x.mutuoId));
            setData(mutuosAMantener);
            setMutuosRecaudar(mutuosSeleccionados);
            setResultRecaudacion(result);
            setShowResult(true);
        }
    }

    const onCalculate = () => {
        let totalMonto = 0;
        let totalOperacion = 0;
        let totalRecaudar = 0;
        for (const mutuoRecaudar of mutuosRecaudar){
            const mutuoSeleccionado = data.find((e) => e.mutuoId === mutuoRecaudar.mutuoId)
            totalMonto += mutuoSeleccionado?.montoOriginal ?? 0;
            totalOperacion += mutuoSeleccionado?.valorOperacion ?? 0;
            totalRecaudar += mutuoSeleccionado?.montoPago ?? 0;
        }
        const calculoEndosoMasivo : CalculateRecaudacionEM = {totalMontoNominal: totalMonto, totalMontoOperacion: totalOperacion, totalMontoRecaudar: totalRecaudar}
        return calculoEndosoMasivo;
    }

    return (
        <Suspense>
            <ModalErrorExcelMasivo 
                onHide={() => {setShowError(false)}}
                show={showError}
                errores={errorProcesamiento}
            />
            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({ ...modalConfirmacion, show: false })
                }
                onConfirm={modalConfirmacion.metodo}
                title={modalConfirmacion.title}
                show={modalConfirmacion.show}
                message={modalConfirmacion.message}
            />
            <ModalResultados 
                open={showResult} 
                onClose={() => setShowResult(false)} 
                title='Resultados recaudación de endosos'
                data={resultRecaudacion} 
                columns={COLUMNS_RESULTADOS}
                columnsExcel={COLUMNS_RESULTADOS}
                excelFileName='RESULTADOS-RECAUDACIÓN-ENDOSOS'
            />
            <Breadcrumb items={breadcrumbItems} />
            <Box sx={{ mt: 2 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <PermissionGuard permissions={[TipoFuncionalidad.Endoso_masivo]}>
                    <CustomAccordion
                        title='Recaudación de endoso másivo'
                        expandedDefault
                    >
                        <FormularioArchivoEndoso onSubmit={onSubmit}/>
                    </CustomAccordion>
                    </PermissionGuard>
                    <Box sx={{ mt: 4 }}>
                        <Tabla
                            data={data}
                            columns={columns}
                        />
                    </Box>
                    <PermissionGuard permissions={[TipoFuncionalidad.Endoso_masivo]}>
                    <CustomAccordion
                        title='Calcular recaudación'
                        expandedDefault
                    >
                        <FormularioCalculoRecaudacionEndoso onSubmit={onRecaudar} onCalculate={onCalculate}
                                                    isFormReseting={isLoading || showResult} mutuosSeleccionados={mutuosRecaudar}/>
                    </CustomAccordion>
                    </PermissionGuard>
                </Paper>
            </Box>
        </Suspense>
    );

}